import { useEffect, useRef } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  buildPendingJobsFromStatus,
  getResolvedJobOutcome,
  hasNewPendingJob
} from 'lib/backgroundJobs';

import type { GetJobsStatusRes } from 'shared-types/responses';
import type { PendingJobEntry } from 'types';

const POLL_DELAYS = [
  5000, 6000, 7000, 8000, 9000, 10000, 12000, 15000, 20000
] as const;

/**
 * - On first mount: fires a cold-start POST (jobIds: null) when not yet initialized,
 *   populates Redux pendingJobs slice, then starts the polling cycle.
 * - While there are pending jobs: polls /admin/background-jobs/status in batches.
 * - When a job reaches a terminal state: dispatches a notice + fires a CustomEvent
 *   + removes the job from the slice.
 * - Skips ticks when the document is hidden; resumes when the tab becomes visible again.
 * - Stops when there are no pending jobs.
 * - Resets the pendingJobs slice when the component unmounts.
 */
const Poller = () => {
  const { dispatch } = useDispatch();
  const { messages, pendingJobs } = useReduxState();

  const delayIndexRef = useRef(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Track the previous set of job ids so we can detect when a new one appears.
  const prevJobKeysRef = useRef<string[]>([]);

  // Keep a ref to messages and pendingJobs.jobs so closures inside setTimeout/
  // event listeners always read the latest value without needing a re-render.
  const messagesRef = useRef(messages);
  const pendingJobsRef = useRef(pendingJobs.jobs);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const coldStart = async () => {
    try {
      if (pendingJobs.initialized) return;

      const response = await api.post<GetJobsStatusRes>(
        '/admin/background-jobs/status',
        { jobIds: null }
      );
      const inflightJobsMap = response.data.jobs ?? {};

      dispatch.setPendingJobs(
        buildPendingJobsFromStatus(inflightJobsMap, messages)
      );
    } catch (err) {
      dispatch.handleErrorDispatch(err);
      dispatch.setPendingJobs({});
    }
  };

  const poll = async (jobs: Record<number, PendingJobEntry>) => {
    try {
      // The timer that fired this poll is spent — null it immediately so
      // scheduleNextTick (called from the visibilitychange listener) can re-arm
      // even if we return early below.
      timerRef.current = null;

      const jobIds = Object.keys(jobs).map(Number);
      if (jobIds.length === 0) return;
      if (document.visibilityState === 'hidden') return;

      const response = await api.post<GetJobsStatusRes>(
        '/admin/background-jobs/status',
        { jobIds }
      );

      const returnedJobsMap = response.data.jobs ?? {};
      const returnedIds = new Set(Object.keys(returnedJobsMap).map(Number));

      // Jobs in slice but absent from response → silently remove.
      jobIds.forEach((id) => {
        if (!returnedIds.has(id)) dispatch.removePendingJob(id);
      });

      Object.values(returnedJobsMap).forEach((job) => {
        const outcome = getResolvedJobOutcome({
          entry: jobs[job.id],
          job,
          messages: messagesRef.current
        });
        if (!outcome) return;

        dispatch.addNotice({ ...outcome.notice, dismissAfter: 0 });
        window.dispatchEvent(
          new CustomEvent('background-job-resolved', {
            detail: outcome.resolution
          })
        );
        dispatch.removePendingJob(job.id);
      });
    } catch (err) {
      dispatch.handleErrorDispatch(err);
    } finally {
      // Advance the backoff index, capped at the last delay.
      delayIndexRef.current = Math.min(
        delayIndexRef.current + 1,
        POLL_DELAYS.length - 1
      );

      scheduleNextTick();
    }
  };

  const scheduleNextTick = () => {
    const jobs = pendingJobsRef.current;
    if (Object.keys(jobs).length === 0) return;
    if (document.visibilityState === 'hidden') return;

    // Guard against double-arming.
    if (timerRef.current !== null) return;

    const delay = POLL_DELAYS[delayIndexRef.current];
    timerRef.current = setTimeout(() => {
      poll(pendingJobsRef.current);
    }, delay);
  };

  // ──────────── Lifecycle effects ────────────────────────────────────────────────────────

  useEffect(() => {
    // Cold-start: fire once when not yet initialized.
    coldStart();

    // Visibility-change listener: resume polling when the tab becomes visible
    // again after being hidden, as long as there are pending jobs and no timer
    // is already armed.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        scheduleNextTick();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup: clear the timer and unbind the visibility-change listener.
      clearTimer();
      dispatch.resetPendingJobs();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Main polling effect: re-runs when the set of job IDs changes.
  useEffect(() => {
    messagesRef.current = messages;
    pendingJobsRef.current = pendingJobs.jobs;

    const currentJobIds = Object.keys(pendingJobs.jobs);
    const hasNewJob = hasNewPendingJob(prevJobKeysRef.current, currentJobIds);
    prevJobKeysRef.current = currentJobIds;

    if (currentJobIds.length === 0) {
      clearTimer();
      return;
    }

    if (hasNewJob) {
      // New job appeared: reset delay and reschedule immediately.
      clearTimer();
      delayIndexRef.current = 0;

      timerRef.current = setTimeout(() => {
        poll(pendingJobsRef.current);
      }, POLL_DELAYS[0]);
    }
  }, [JSON.stringify(Object.keys(pendingJobs.jobs).sort())]);

  return null;
};

/**
 * Gatekeeper component that mounts once inside <Provider> in _app.tsx.
 * - Skips polling when the user is not authenticated or not an admin.
 */
const PendingJobsPoller = () => {
  const { user } = useReduxState();
  if (!user || !user.roles.includes('admin')) return null;

  return <Poller />;
};

export default PendingJobsPoller;
